// SignupGoogleOption is the Google block under the signup form's submit button
// (docs/google-sign-in.md): an "Or skip the form" divider, then a pale card with
// the caption "Use your Google account" and Google's button filling the card,
// drawn 10% larger than GIS's large size so its text isn't too faint.
// Every visitor gets it: it is not behind a PostHog experiment or a feature flag.
//
// The button inside the card is Google's own iframe. Google decides what it says:
// a browser whose Google session has already approved Kick gets the personalized
// "Sign in as <name>" pill with the account's email and avatar; everyone else gets
// "Sign up with Google". Nothing here can force the personalized version.
//
// SignUp is a class component, which is why this lives in its own function
// component. It owns three decisions and nothing else:
//
//   - WHETHER to show the block: the signup form must be live (`pageReady`, set
//     once SignUp has decided not to redirect the visitor or offer "Continue as
//     …", so Google's script never loads for someone who is about to leave),
//     Google sign-in must be able to work here (`isGoogleSignInAvailable`: a
//     client id is configured and this is not an in-app webview), the parent
//     must not have asked to hide it (`hidden`: the name_required fallback, or a
//     503 from the server), and Google's script must have loaded (`unavailable`
//     flips on any failure and takes the divider and the card down with the
//     button).
//   - WHEN the button is live: consent first. Until the terms box is ticked and
//     a state is chosen, the GIS iframe sits behind a labelled overlay; a click
//     there touches both fields and runs validation so the form shows what is
//     missing, exactly as pressing the submit button would. The overlay is an
//     affordance, not the guarantee -- SignUp re-validates when the credential
//     arrives, because keyboard activation can reach the iframe past it.
//   - WHERE the credential goes: straight to `onCredential`. Nothing here logs,
//     traces or stores it.

import { useState } from "react";

import GoogleSignInButton, {
  GSI_BUTTON_MAX_WIDTH,
} from "~/shared/components/GoogleSignInButton.jsx";
import {
  kickDividerLine,
  kickNavy,
  kickPaleLavender,
} from "~/shared/styles/baseStyles.js";
import { isGoogleSignInAvailable } from "~/shared/utils/googleSignInAvailability";
import { getGoogleSignInClientID } from "~/shared/utils/sharedUtils";

export const GOOGLE_SIGNUP_GATED_LABEL =
  "Sign up with Google — agree to the terms and choose your state first";

export const GOOGLE_SIGNUP_DIVIDER_TEXT = "Or skip the form";
export const GOOGLE_SIGNUP_CAPTION = "Use your Google account";

// GIS has no font-size option and "large" is its biggest button, so the whole
// button is drawn 10% larger: 44px tall, still filling the card
// (GoogleSignInButton's `scale`). Added 2026-11-03: at 1x the text looked too
// faint next to the form.
export const GOOGLE_SIGNUP_BUTTON_SCALE = 1.1;

const CAPTION_ID = "google-signup-caption";
const CARD_PADDING_X = 12;

const styles = {
  block: {
    width: "100%",
    // Google's widest button plus the card's side padding: on a phone the block
    // spans the form, on the wide desktop column it hugs the button.
    maxWidth: GSI_BUTTON_MAX_WIDTH + 2 * CARD_PADDING_X,
    margin: "20px auto 16px",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "16px",
  },
  rule: {
    flex: 1,
    borderTop: `1px solid ${kickDividerLine}`,
  },
  dividerText: {
    color: kickNavy,
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: 1.2,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  card: {
    backgroundColor: kickPaleLavender,
    borderRadius: "12px",
    padding: `16px ${CARD_PADDING_X}px 12px`,
    textAlign: "center",
  },
  caption: {
    color: kickNavy,
    fontSize: "16px",
    lineHeight: 1.3,
    margin: "0 0 12px",
  },
};

// isGoogleSignupGated mirrors the two Yup rules the Google path cannot skip:
// `terms` must be exactly true and a state must be chosen.
export function isGoogleSignupGated(values) {
  return !(values?.terms === true && values?.usState?.value);
}

export default function SignupGoogleOption({
  pageReady,
  formik,
  hidden = false,
  onCredential,
}) {
  const [unavailable, setUnavailable] = useState(false);

  if (!pageReady || hidden || unavailable || !isGoogleSignInAvailable()) {
    return null;
  }

  return (
    <div style={styles.block}>
      <div data-testid="google-signup-divider" style={styles.divider}>
        <span aria-hidden="true" style={styles.rule} />
        <span style={styles.dividerText}>{GOOGLE_SIGNUP_DIVIDER_TEXT}</span>
        <span aria-hidden="true" style={styles.rule} />
      </div>
      <div
        data-testid="google-signup-card"
        role="group"
        aria-labelledby={CAPTION_ID}
        style={styles.card}
      >
        <p id={CAPTION_ID} style={styles.caption}>
          {GOOGLE_SIGNUP_CAPTION}
        </p>
        <GoogleSignInButton
          clientID={getGoogleSignInClientID()}
          text="signup_with"
          width={GSI_BUTTON_MAX_WIDTH}
          scale={GOOGLE_SIGNUP_BUTTON_SCALE}
          gated={isGoogleSignupGated(formik.values)}
          gatedLabel={GOOGLE_SIGNUP_GATED_LABEL}
          onGatedClick={() => {
            formik.setTouched(
              { ...formik.touched, terms: true, usState: true },
              false,
            );
            formik.validateForm();
          }}
          onCredential={onCredential}
          onUnavailable={() => setUnavailable(true)}
        />
      </div>
    </div>
  );
}
